import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ClashReveal from './ClashReveal';
import {
  DUNGEON,
  FINAL_DEPTH,
  MAX_HP,
  TOOLS,
  applyChisel,
  applyMemory,
  applyMirror,
  applySwap,
  continueBattle,
  continueDungeon,
  createGame,
  enterRoom,
  getClashPreview,
  getRoom,
  getRooms,
  resolveClash,
  rollClash,
  startRun,
} from './game';
import './DiceDungeon.css';

const STORAGE_KEY = 'slashRoll.diceDungeon.v1';
const PHASES = [
  'intro',
  'map',
  'ready',
  'rolled',
  'result',
  'roomClear',
  'won',
  'lost',
];

const validRoll = (roll) => Number.isInteger(roll) && roll >= 0 && roll <= 99;

const isValidSave = (saved) => {
  if (
    !saved ||
    !PHASES.includes(saved.phase) ||
    !Number.isInteger(saved.depth) ||
    saved.depth < 1 ||
    saved.depth > FINAL_DEPTH ||
    !Number.isInteger(saved.hp) ||
    saved.hp < 0 ||
    saved.hp > MAX_HP ||
    !saved.tools ||
    !Object.keys(TOOLS).every(
      (id) => Number.isInteger(saved.tools[id]) && saved.tools[id] >= 0
    ) ||
    !Array.isArray(saved.clearedRooms) ||
    !Array.isArray(saved.history) ||
    (saved.storedRoll !== null && !validRoll(saved.storedRoll))
  )
    return false;

  const roomPhases = ['ready', 'rolled', 'result', 'roomClear', 'won', 'lost'];
  if (roomPhases.includes(saved.phase)) {
    const roomExistsHere = DUNGEON[saved.depth - 1].some(
      (room) => room.id === saved.roomId
    );
    if (
      !roomExistsHere ||
      !Number.isInteger(saved.enemyHp) ||
      saved.enemyHp < 0
    )
      return false;
  }
  if (
    saved.phase === 'rolled' &&
    (!validRoll(saved.playerRoll) || !validRoll(saved.enemyRoll))
  )
    return false;
  if (
    ['result', 'roomClear', 'won', 'lost'].includes(saved.phase) &&
    !saved.result
  )
    return false;
  return true;
};

const loadGame = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (isValidSave(saved)) return { ...createGame(), ...saved };
  } catch {
    // A damaged or unavailable save should not prevent a new run.
  }
  return createGame();
};

const DiceDungeon = () => {
  const [game, setGame] = useState(loadGame);
  const room = getRoom(game);
  const preview = getClashPreview(game);
  const reduceMotion = !!window.matchMedia?.('(prefers-reduced-motion: reduce)')
    .matches;

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(game));
    } catch {
      // The run remains playable if browser storage is disabled.
    }
  }, [game]);

  const makeRoll = () => Math.floor(Math.random() * 100);
  const beginClash = () =>
    setGame((current) => rollClash(current, makeRoll(), makeRoll()));
  const chiselDigit = (index) =>
    setGame((current) =>
      applyChisel(current, index, Math.floor(Math.random() * 10))
    );
  const memoryRune = () =>
    setGame((current) => applyMemory(current, makeRoll()));

  return (
    <main className="dungeonPage">
      <header className="dungeonTopbar">
        <Link to="/single-player">← Games</Link>
        <span>SLASH ROLL / DICE DUNGEON</span>
        <Link to="/">Home</Link>
      </header>

      <div className="dungeonLayout">
        <div className="dungeonMain">
          {game.phase === 'intro' && (
            <div className="dungeonHeading">
              <span className="dungeonEyebrow">A FOUR CHAMBER DESCENT</span>
              <h1>Dice Dungeon</h1>
              <p>
                Choose a path, roll against its guardian, and spend scarce
                artifacts to turn bad numbers into winning ones.
              </p>
            </div>
          )}

          {game.phase === 'intro' && (
            <section className="dungeonPanel">
              <span className="dungeonEyebrow">THE GATE IS OPEN</span>
              <h2>Outroll everything below.</h2>
              <p>
                You and each guardian roll from 00 to 99. The higher roll
                strikes. Winning with matching digits deals two damage; an enemy
                pair hits one point harder. A tie wounds both sides.
              </p>
              <p>
                You may use one artifact per clash. Artifacts are consumed, and
                your path determines which ones you find. Clear three chambers,
                then destroy the Dungeon Heart.
              </p>
              <button
                className="dungeonPrimary"
                onClick={() => setGame(startRun)}
              >
                Descend →
              </button>
              {(game.bestDepth > 0 || game.victories > 0) && (
                <p className="dungeonRecord">
                  Deepest chamber: {game.bestDepth} / {FINAL_DEPTH} · Escapes:{' '}
                  {game.victories}
                </p>
              )}
            </section>
          )}

          {game.phase === 'map' && (
            <section className="dungeonPanel">
              <span className="dungeonEyebrow">
                DEPTH {game.depth} / {FINAL_DEPTH}
              </span>
              <h2>
                {game.depth === FINAL_DEPTH
                  ? 'Only the Heart remains'
                  : 'Choose the next chamber'}
              </h2>
              <div
                className="dungeonPath"
                aria-label={`Dungeon progress: chamber ${game.depth} of ${FINAL_DEPTH}`}
              >
                {DUNGEON.map((_, index) => (
                  <span
                    key={index}
                    className={
                      index + 1 < game.depth
                        ? 'cleared'
                        : index + 1 === game.depth
                        ? 'current'
                        : ''
                    }
                  >
                    {index + 1}
                  </span>
                ))}
              </div>
              <div className="dungeonRoomGrid">
                {getRooms(game).map((candidate) => (
                  <button
                    className="dungeonRoom"
                    key={candidate.id}
                    onClick={() =>
                      setGame((current) => enterRoom(current, candidate.id))
                    }
                  >
                    <span className="dungeonEyebrow">
                      {candidate.title.toUpperCase()}
                    </span>
                    <strong>{candidate.name}</strong>
                    <span>{candidate.description}</span>
                    <div className="dungeonRoomStats">
                      <span>HP {candidate.hp}</span>
                      <span>Damage {candidate.damage}</span>
                    </div>
                    {candidate.reward && (
                      <span className="dungeonReward">
                        Reward: {TOOLS[candidate.reward.tool].name} ×
                        {candidate.reward.amount}
                      </span>
                    )}
                    <span className="dungeonEnter">Enter chamber →</span>
                  </button>
                ))}
              </div>
            </section>
          )}

          {game.phase === 'ready' && room && (
            <section className="dungeonPanel dungeonEncounter">
              <span className="dungeonEyebrow">
                DEPTH {game.depth} / {room.title.toUpperCase()}
              </span>
              <h2>{room.name} blocks the way</h2>
              <div className="dungeonEnemyPortrait" aria-hidden="true">
                <span>{room.name.slice(0, 1)}</span>
              </div>
              <p>{room.description}</p>
              <div className="dungeonHealthPair">
                <span>You: {game.hp} HP</span>
                <span>
                  {room.name}: {game.enemyHp} HP
                </span>
              </div>
              <button className="dungeonPrimary" onClick={beginClash}>
                Roll for combat →
              </button>
            </section>
          )}

          {game.phase === 'rolled' && room && preview && (
            <ClashReveal
              game={game}
              room={room}
              preview={preview}
              onMirror={() => setGame(applyMirror)}
              onChisel={chiselDigit}
              onSwap={() => setGame(applySwap)}
              onMemory={memoryRune}
              onResolve={() => setGame(resolveClash)}
            />
          )}

          {['result', 'roomClear', 'won', 'lost'].includes(game.phase) &&
            game.result &&
            room && (
              <motion.section
                className={`dungeonPanel dungeonOutcome dungeonOutcome-${
                  game.result.outcome
                } ${game.phase === 'won' ? 'dungeonWon' : ''} ${
                  game.phase === 'lost' ? 'dungeonLost' : ''
                }`}
                initial={
                  reduceMotion ? false : { opacity: 0, scale: 0.95, y: 24 }
                }
                animate={reduceMotion ? {} : { opacity: 1, scale: 1, y: 0 }}
              >
                <span className="dungeonEyebrow">
                  {String(game.result.playerRoll).padStart(2, '0')} VS{' '}
                  {String(game.result.enemyRoll).padStart(2, '0')}
                </span>
                <h2>
                  {game.phase === 'won'
                    ? 'The Dungeon Heart shatters!'
                    : game.phase === 'lost'
                    ? 'The dungeon keeps you'
                    : game.phase === 'roomClear'
                    ? `${room.name} is defeated`
                    : game.result.outcome === 'strike'
                    ? 'Your strike lands'
                    : game.result.outcome === 'hit'
                    ? `${room.name} strikes`
                    : 'Both rolls collide'}
                </h2>
                <p className="dungeonDamageReport">
                  {game.result.damageToEnemy > 0 &&
                    `${room.name} takes ${game.result.damageToEnemy} damage.`}
                  {game.result.damageToEnemy > 0 &&
                    game.result.damageToPlayer > 0 &&
                    ' '}
                  {game.result.damageToPlayer > 0 &&
                    `You take ${game.result.damageToPlayer} damage.`}
                </p>
                <div className="dungeonHealthPair">
                  <span>You: {game.hp} HP</span>
                  <span>
                    {room.name}: {game.enemyHp} HP
                  </span>
                </div>
                {game.phase === 'result' && (
                  <button
                    className="dungeonPrimary"
                    onClick={() => setGame(continueBattle)}
                  >
                    Fight on →
                  </button>
                )}
                {game.phase === 'roomClear' && (
                  <>
                    {game.result.reward && (
                      <p className="dungeonLoot">
                        Found: {TOOLS[game.result.reward.tool].name} ×
                        {game.result.reward.amount}
                      </p>
                    )}
                    <button
                      className="dungeonPrimary"
                      onClick={() => setGame(continueDungeon)}
                    >
                      Go deeper →
                    </button>
                  </>
                )}
                {['won', 'lost'].includes(game.phase) && (
                  <>
                    <p>
                      Deepest chamber: {game.bestDepth} / {FINAL_DEPTH} ·
                      Escapes: {game.victories}
                    </p>
                    <button
                      className="dungeonPrimary"
                      onClick={() => setGame(startRun)}
                    >
                      New descent →
                    </button>
                  </>
                )}
              </motion.section>
            )}
        </div>

        {game.phase !== 'intro' && (
          <aside className="dungeonSidebar">
            <div className="dungeonSidePanel">
              <span className="dungeonEyebrow">EXPLORER</span>
              <div className="dungeonStat">
                <span>Depth</span>
                <strong>
                  {game.depth} / {FINAL_DEPTH}
                </strong>
              </div>
              <div className="dungeonStat">
                <span>HP</span>
                <strong>
                  {game.hp} / {MAX_HP}
                </strong>
              </div>
              <div className="dungeonHpBar">
                <span style={{ width: `${(game.hp / MAX_HP) * 100}%` }} />
              </div>
              {game.storedRoll !== null && (
                <div className="dungeonStored">
                  <span>Memory Rune</span>
                  <strong>{String(game.storedRoll).padStart(2, '0')}</strong>
                </div>
              )}
            </div>
            <div className="dungeonSidePanel">
              <span className="dungeonEyebrow">ARTIFACTS</span>
              {Object.entries(TOOLS).map(([id, tool]) => (
                <div className="dungeonInventory" key={id}>
                  <div>
                    <strong>{tool.name}</strong>
                    <span>{tool.description}</span>
                  </div>
                  <b>{game.tools[id]}</b>
                </div>
              ))}
            </div>
            {game.history.length > 0 && (
              <div className="dungeonSidePanel">
                <span className="dungeonEyebrow">CLASH LOG</span>
                {game.history.slice(0, 4).map((entry, index) => (
                  <p
                    className="dungeonHistory"
                    key={`${entry.roomName}-${index}`}
                  >
                    <span>
                      {String(entry.playerRoll).padStart(2, '0')} /{' '}
                      {String(entry.enemyRoll).padStart(2, '0')}
                    </span>
                    <strong>
                      {entry.outcome === 'strike'
                        ? 'HIT'
                        : entry.outcome === 'hit'
                        ? 'HURT'
                        : 'TIE'}
                    </strong>
                  </p>
                ))}
              </div>
            )}
          </aside>
        )}
      </div>
    </main>
  );
};

export default DiceDungeon;
